/**
 * Shared utility functions for dashboard widget layout calculations
 * Used by both DashboardEditor and DashboardViewer to ensure consistent layout rendering
 */

/**
 * Calculate grid layouts from widget data
 * Converts widget layout data to react-grid-layout format
 * 
 * @param {Array} widgets - Array of widget objects with layout properties
 * @returns {Object} Layouts object for react-grid-layout (keyed by breakpoint)
 */
export function calculateGridLayouts(widgets) {
  if (!widgets || widgets.length === 0) {
    return { lg: [] };
  }

  return {
    lg: widgets.map((widget) => {
      const w = widget.layout?.w ?? 12; // Default: 12/24 columns (50% width)
      const h = widget.layout?.h ?? 6; // Default: 6 rows
      const x = widget.layout?.x ?? 0;
      const y = widget.layout?.y ?? 0;

      return {
        i: String(widget.id),
        x,
        y,
        w: Math.max(w, 1), // Minimum width of 1 column
        h: Math.max(h, 1), // Minimum height of 1 row
        minW: 1,
        minH: 1,
      };
    }),
  };
}

/**
 * Normalize widget data for display
 * Ensures all widgets have valid layout properties
 * 
 * @param {Array} widgets - Array of widget objects from API
 * @returns {Array} Normalized widget array
 */
export function normalizeWidgets(widgets) {
  if (!widgets || widgets.length === 0) {
    return [];
  }

  return widgets.map((widget) => {
    const layoutX = widget.layout?.x ?? 0;
    const layoutY = widget.layout?.y ?? 0;
    const layoutW = widget.layout?.w ?? 12; // Default: 12/24 columns (50% width)
    const layoutH = widget.layout?.h ?? 6; // Default: 6 rows

    return {
      ...widget,
      layout: {
        x: layoutX,
        y: layoutY,
        w: Math.max(layoutW, 1), // Minimum width of 1 column
        h: Math.max(layoutH, 1), // Minimum height of 1 row
      },
    };
  });
}

/**
 * Grid configuration constants shared between Editor and Viewer
 */
export const GRID_CONFIG = {
  breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
  cols: { lg: 24, md: 24, sm: 12, xs: 8, xxs: 4 },
  rowHeight: 60,
  margin: [16, 16],
  containerPadding: [16, 16],
};


